import { Fragment, PropsWithChildren, ReactNode, useEffect } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { IconClose } from '@/components/svg-symbols/icons';
import { cn } from '@/utils/cn';
import { useRouteHash } from '@/utils/use-route-hash';
import { useDisableBodyScroll } from './utils/use-disable-body-scroll';
import { IconArrowLeft, IconInfoCircle, IconTickCircle } from '../icons';
interface Props extends PropsWithChildren {
  isOpen: boolean;
  title: string | ReactNode;
  handleClose: () => void;
  width?: string;
  className?: string;
  closable?: boolean;
  backHandler?: () => void;
  id?: string;
  theme?: 'light' | 'dark';
  type?: 'success' | 'warning' | 'default';
  displayType?: 'default' | 'withTopBar' | 'withLeftBorder';
  parentClassName?: string;
  bodyClassName?: string;
  headClassName?: string;
  disableBodyScrollOnOpen?: boolean;
  backgroundImage?: string;
  modalHash?: string;
}
const typeIcons = {
  success: <IconTickCircle className='ml-1 h-6 w-6 text-alert-success-dark' />,
  warning: <IconInfoCircle className='ml-1 h-6 w-6 text-alert-warning-dark' />,
  default: ''
};
export function Modal({
  isOpen,
  title,
  children,
  handleClose,
  width = 'w-full lg:max-w-md',
  className,
  closable = true,
  backHandler,
  id,
  theme = 'light',
  type = 'default',
  displayType = 'default',
  parentClassName = '',
  bodyClassName = '',
  headClassName = '',
  disableBodyScrollOnOpen = false,
  backgroundImage,
  modalHash
}: Props) {
  const {
    shouldDisableScroll,
    disableScroll,
    enableScroll
  } = useDisableBodyScroll(disableBodyScrollOnOpen);
  const {
    addHash,
    removeHash
  } = useRouteHash({
    hash: modalHash,
    onHashRemoved: modalHash ? handleClose : undefined
  });
  const closeHandler = () => {
    closable && handleClose();
    !shouldDisableScroll && enableScroll();
  };
  useEffect(() => {
    if (!modalHash) return;
    if (isOpen) addHash();else if (window.location.hash === `#${modalHash}`) {
      removeHash();
    }
  }, [addHash, isOpen, modalHash, removeHash]);
  useEffect(() => {
    if (!modalHash) return;
    return () => {
      if (window.location.hash === `#${modalHash}`) removeHash();
    };
  }, [modalHash, removeHash]);
  return <Transition appear show={isOpen} as={Fragment} afterEnter={disableScroll} beforeLeave={enableScroll} data-sentry-element="Transition" data-sentry-component="Modal" data-sentry-source-file="modal.tsx">
            <Dialog id={id} as='div' className={cn('relative z-top-of-navigation-bar', parentClassName)} onClose={closeHandler} initialFocus={undefined} data-sentry-element="Dialog" data-sentry-source-file="modal.tsx">
                <TransitionChild as={Fragment} enter='ease-out duration-300' enterFrom='opacity-0' enterTo='opacity-100' leave='ease-in duration-200' leaveFrom='opacity-100' leaveTo='opacity-0' data-sentry-element="TransitionChild" data-sentry-source-file="modal.tsx">
                    <div className='fixed inset-0 bg-black bg-opacity-25' />
                </TransitionChild>

                <div className='fixed inset-0'>
                    <div className='flex min-h-full items-center justify-center p-4 text-center'>
                        <TransitionChild as={Fragment} enter='lg:ease-out lg:duration-300 transition ease duration-300 transform lg:transform-none' enterFrom='lg:opacity-0 lg:scale-95 translate-y-full' enterTo='lg:opacity-100 lg:scale-100 opacity-100 translate-y-0' leave='lg:ease-in lg:duration-200 transition ease duration-200 transform lg:transform-none' leaveFrom='lg:opacity-100 lg:scale-100 opacity-100 translate-y-0' leaveTo='lg:opacity-0 lg:scale-95 translate-y-full' data-sentry-element="TransitionChild" data-sentry-source-file="modal.tsx">
                            <DialogPanel className={cn('absolute bottom-0 flex w-4/5 transform flex-col justify-between text-right shadow-lg transition-all lg:relative lg:h-fit lg:rounded-xl', {
              'bg-white': theme === 'light',
              'bg-gray-700': theme === 'dark'
            }, width, displayType === 'withTopBar' ? 'rounded-t-none' : 'rounded-t-xl', className)} style={{
              backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined
            }} data-sentry-element="DialogPanel" data-sentry-source-file="modal.tsx">
                                <div className={cn(
              // eslint-disable-next-line no-nested-ternary
              displayType === 'default' ? 'z-10 mb-2 flex h-8 w-full items-center justify-between px-3 pt-2 lg:mb-3 lg:px-4 lg:pt-4' : displayType === 'withLeftBorder' ? 'z-10 mb-2 flex w-full items-start justify-between px-3 pt-2 lg:mb-3 lg:px-4 lg:pt-4' : 'relative flex h-14 w-full justify-center p-4 text-text-black shadow-sm lg:h-8 lg:justify-between lg:pb-0 lg:shadow-none')}>
                                    <div className={cn('flex items-center', headClassName)}>
                                        {typeIcons[type]}
                                        <DialogTitle as='h3' className={cn('line-clamp-1', {
                    'mx-auto text-center': !closable
                  }, {
                    'text-text-black': theme === 'light',
                    'text-white': theme === 'dark'
                  }, displayType === 'withTopBar' ? 'text-body-16 font-medium lg:font-bold' : 'text-body-14 font-bold lg:text-body-16')} data-sentry-element="DialogTitle" data-sentry-source-file="modal.tsx">
                                            {title}
                                        </DialogTitle>
                                    </div>

                                    {closable && !backHandler && <IconClose className={cn('h-6 w-6 min-w-6 cursor-pointer',
                // eslint-disable-next-line no-nested-ternary
                theme === 'dark' ? 'text-white' :
                // eslint-disable-next-line no-nested-ternary
                displayType === 'withLeftBorder' ? 'text-text-darkGray' : displayType === 'default' ? 'text-text-mediumGray' : 'absolute left-3 top-1/2 -translate-y-1/2 text-text-black lg:static lg:translate-y-0 lg:text-text-mediumGray')} onClick={closeHandler} />}

                                    {!!backHandler && <IconArrowLeft className={cn('h-6 w-6 cursor-pointer',
                // eslint-disable-next-line no-nested-ternary
                theme === 'dark' ? 'text-white' : displayType === 'default' ? 'text-text-mediumGray' : 'absolute right-3 top-1/2 -translate-y-1/2 rotate-180 text-text-black lg:static lg:translate-y-0 lg:rotate-0 lg:text-text-mediumGray')} onClick={backHandler} />}
                                </div>

                                <div className={cn('max-h-[calc(100vh-64px)] w-full grow overflow-y-auto px-3 pb-4 lg:max-h-[89vh] lg:px-4 lg:pb-6', bodyClassName)}>
                                    {children}
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>;
}